// The Fleet page's ONE flood guard (#6753, epic #6552; prototype `DEBOUNCE ... LOCK_MS`
// `:1383`, `queue` / tripLock / `commit` `:2011-2066`). Owner ruling (ivndev001,
// 2026-10-20) on the flood guard, which covers EVERY card control: job Pause, Resume and
// Stop; Pause all and Resume all; epic Pause, Resume and Stop; the epic model picker; and
// (#6766) the capability switches:
//
//   - a change shows at once and is SENT after 700 ms without another click on that control;
//     a run that ends where it started ("on, off, on") sends nothing;
//   - a control ignores clicks while its change saves;
//   - at most 6 changes are sent per rolling 30 000 ms; the 7th locks every control on the
//     page for 15 000 ms, with a countdown;
//   - a server 429 locks the controls for the seconds it names (`retry_after`).
//
// ONE INSTANCE PER PAGE. `FleetMachines` creates it (`flood-guard-context.ts`) and hands it to
// every card through context; a card, a zone or a control never makes its own. Two
// instances would each let six through, and "the 7th locks every control on the page"
// would be false. #6766 sends its switches through this same instance.
//
// THE COUNTING IS KIT'S: the rolling window is `createInMemoryRateLimiter` from
// `@developerz/kit`, keyed once for the page, never a hand-rolled copy (the repo's one
// sliding window; `scripts/one-impl.test.ts` matches only `*RateLimiter*` names, so a copy
// named otherwise would get past it and the review must catch it). Its `hit` is async and
// records a hit only when it admits one.
//
// THE WINDOW IS NOT CLEARED WHEN A LOCK LIFTS. The prototype empties its log on unlock, which
// let a 7th send through inside 30 s of the first six; the ruling bounds sends per ROLLING
// 30 s, and the pause routes' own server bound counts the same way (#6751, 429
// `pause-rate-limited`). So a send right after a lock, while the six are still in the
// window, locks again.
//
// A STOP'S DEBOUNCE IS ITS UNDO WINDOW. A confirmed Stop is a change with `delayMs` 6 500 and
// `outlivesLock`: Undo (`cancel`) sends nothing, and a Stop confirmed before a lock starts
// still sends when its window ends (a choice no ruling covers, made in #6753). It outlives the
// LOCK, never the bound: a Stop the window refuses stays queued and is sent when the oldest
// send ages out, so no more than six are ever sent in a rolling 30 s.
//
// Pure: the clock and the timers are injected (`FloodTimers`), nothing here reads the DOM or
// a signal, and `subscribe` is how a view learns that something it shows moved.

import { createInMemoryRateLimiter, type RateLimiter } from '@developerz/kit';

export const FLOOD_DEBOUNCE_MS = 700;
export const FLOOD_LIMIT = 6;
export const FLOOD_WINDOW_MS = 30_000;
export const FLOOD_LOCK_MS = 15_000;
/** A Stop's Undo window: the cancel is sent when it ends (prototype stopNoteHTML). */
export const STOP_UNDO_MS = 6_500;

/** The one key the page's sends are counted under. */
const PAGE_KEY = 'fleet-page';

export type FloodTimers = {
  readonly now: () => number;
  readonly setTimeout: (fn: () => void, ms: number) => unknown;
  readonly clearTimeout: (handle: unknown) => void;
  readonly setInterval: (fn: () => void, ms: number) => unknown;
  readonly clearInterval: (handle: unknown) => void;
};

/** Why a queued change ended without being sent. */
export type DropReason = 'unchanged' | 'locked' | 'cancelled';

export type GuardedChange<V> = {
  /** The stored value when this run of clicks began (what the read says). */
  readonly from: V;
  /** The value the latest click asks for. */
  readonly to: V;
  /** Sends `to` once the wait ends; resolves when the server has answered. */
  readonly send: (value: V) => Promise<void>;
  /** The wait before sending: {@link FLOOD_DEBOUNCE_MS}, or a Stop's {@link STOP_UNDO_MS}. */
  readonly delayMs?: number;
  /** A lock that starts during the wait does not drop it (a confirmed Stop). */
  readonly outlivesLock?: boolean;
  /** The change ended unsent. Not called for one that was sent, whatever it answered. */
  readonly onDropped?: (why: DropReason) => void;
  /** Value equality; `Object.is` by default. */
  readonly equals?: (a: V, b: V) => boolean;
};

/** What a click did: queued (shown at once), or ignored because the page is locked or the control saves. */
export type ChangeVerdict = 'queued' | 'locked' | 'saving';

export type FloodGuard = {
  change<V>(key: string, change: GuardedChange<V>): ChangeVerdict;
  /** Undo: drop the control's waiting change; nothing is sent. */
  cancel(key: string): void;
  /**
   * The value a waiting or in-flight change asks for, shown until the server answers;
   * undefined when none waits.
   */
  wanted(key: string): unknown;
  /** True while the control's change is in flight: it ignores clicks. */
  saving(key: string): boolean;
  /** Whole seconds until the page unlocks; 0 when unlocked. */
  lockedSeconds(): number;
  /** A server 429: lock for `seconds`, never shortening a longer lock. */
  lockFor(seconds: number): void;
  subscribe(listener: () => void): () => void;
  /** The page left: drop every waiting change (nothing is sent) and stop every timer. */
  dispose(): void;
};

type Waiting = {
  readonly change: GuardedChange<unknown>;
  readonly timer: unknown;
};

export function createFloodGuard(
  timers: FloodTimers,
  limiter: RateLimiter = createInMemoryRateLimiter(timers.now),
): FloodGuard {
  const waiting = new Map<string, Waiting>();
  /** The value each in-flight change sent: still shown (and `wanted`) until it answers. */
  const inFlight = new Map<string, unknown>();
  const listeners = new Set<() => void>();
  let lockUntil = 0;
  let ticker: unknown = null;
  let disposed = false;

  const notify = (): void => {
    for (const fn of [...listeners]) fn();
  };
  const isLocked = (): boolean => timers.now() < lockUntil;
  const same = <V>(c: GuardedChange<V>, a: V, b: V): boolean => (c.equals ?? Object.is)(a, b);

  const drop = (key: string, why: DropReason): void => {
    const w = waiting.get(key);
    if (w === undefined) return;
    timers.clearTimeout(w.timer);
    waiting.delete(key);
    w.change.onDropped?.(why);
  };

  const stopTicker = (): void => {
    if (ticker === null) return;
    timers.clearInterval(ticker);
    ticker = null;
  };

  const lockUntilAt = (until: number): void => {
    if (until <= lockUntil) return;
    lockUntil = until;
    for (const [key, w] of [...waiting]) if (w.change.outlivesLock !== true) drop(key, 'locked');
    if (ticker === null) {
      ticker = timers.setInterval(() => {
        if (!isLocked()) stopTicker();
        notify();
      }, 1_000);
    }
    notify();
  };

  const send = async (key: string, change: GuardedChange<unknown>): Promise<void> => {
    inFlight.set(key, change.to);
    notify();
    try {
      await change.send(change.to);
    } finally {
      inFlight.delete(key);
      if (!disposed) notify();
    }
  };

  const commit = async (key: string): Promise<void> => {
    const w = waiting.get(key);
    if (w === undefined) return;
    waiting.delete(key);
    const { change } = w;
    const outlives = change.outlivesLock === true;
    if (isLocked() && !outlives) {
      change.onDropped?.('locked');
      notify();
      return;
    }
    inFlight.set(key, change.to);
    notify();
    const verdict = await limiter.hit(PAGE_KEY, FLOOD_LIMIT, FLOOD_WINDOW_MS);
    inFlight.delete(key);
    if (disposed) return;
    if (!verdict.ok) {
      lockUntilAt(timers.now() + FLOOD_LOCK_MS);
      if (!outlives) {
        change.onDropped?.('locked');
        notify();
        return;
      }
      // A confirmed Stop outlives the LOCK, never the bound: it stays queued (still shown,
      // still undoable) and tries again when the window frees a slot (`resetSeconds`).
      const retry = timers.setTimeout(() => void commit(key), verdict.resetSeconds * 1_000);
      waiting.set(key, { change, timer: retry });
      notify();
      return;
    }
    await send(key, change);
  };

  return {
    change(key, change) {
      if (disposed || inFlight.has(key)) return 'saving';
      if (isLocked()) return 'locked';
      const prior = waiting.get(key);
      const from = prior === undefined ? change.from : (prior.change.from as typeof change.from);
      if (prior !== undefined) timers.clearTimeout(prior.timer);
      if (same(change, from, change.to)) {
        // Back where the run began: nothing to send.
        waiting.delete(key);
        (prior?.change ?? change).onDropped?.('unchanged');
        notify();
        return 'queued';
      }
      const merged: GuardedChange<unknown> = { ...change, from } as GuardedChange<unknown>;
      const timer = timers.setTimeout(() => void commit(key), change.delayMs ?? FLOOD_DEBOUNCE_MS);
      waiting.set(key, { change: merged, timer });
      notify();
      return 'queued';
    },
    cancel(key) {
      drop(key, 'cancelled');
      notify();
    },
    wanted: (key) => (waiting.has(key) ? waiting.get(key)?.change.to : inFlight.get(key)),
    saving: (key) => inFlight.has(key),
    lockedSeconds: () => (isLocked() ? Math.ceil((lockUntil - timers.now()) / 1_000) : 0),
    lockFor(seconds) {
      if (disposed || !Number.isFinite(seconds) || seconds <= 0) return;
      lockUntilAt(timers.now() + seconds * 1_000);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    dispose() {
      disposed = true;
      for (const [key, w] of [...waiting]) {
        timers.clearTimeout(w.timer);
        waiting.delete(key);
      }
      stopTicker();
      listeners.clear();
    },
  };
}

/** The browser's clock and timers, read at call time so a test's fake timers apply. */
export const BROWSER_TIMERS: FloodTimers = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (h) => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>),
  setInterval: (fn, ms) => globalThis.setInterval(fn, ms),
  clearInterval: (h) => globalThis.clearInterval(h as ReturnType<typeof setInterval>),
};
